import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create or update assembly service configuration for a services provider
 */
export const createAssemblyService = async (
	servicesProviderId: string,
	data: Record<string, unknown>,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.assembly(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for creating or updating assembly service configuration for a services provider
 */
export const useUpdateAssemblyService = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: Record<string, unknown>) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create assembly service without a services provider ID.',
				);
			}
			return createAssemblyService(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Update the cache directly with the response data
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
